<template>
  <div class="updated-appointment-row">
    <div class="text-center">
      <div v-if="showStatus" :class="statusClassList">{{ statusText }}</div>
      <div style="font-size: 36px; line-height: 1.2;">{{ dateText }}</div>
      <div style="font-size: 18px; line-height: 1.2;">{{ monthText }}</div>
      <div style="font-size: 14px; line-height: 1.2;">{{ currentAppointmentTime }}</div>
    </div>
    <div>
      <router-link :to="route" class="title">{{ service }}</router-link>
      <div class="subtitle">{{ optionTitle }}</div>
      <div class="subtitle">{{ provider }}</div>
      <div class="subtitle">{{ user.name }} - {{ user.phoneNumber }}</div>
      <div class="subtitle">{{ home.addressNo }} {{ home.project }}</div>
    </div>
    <div v-if="isFulfilledStatus" class="row">
      <div class="col-4">
        <div class="label mb-2">วันที่ใบเสร็จ</div>
        <el-date-picker v-model="receiptDate" class="date"/>
      </div>
      <div class="col-3">
        <div class="label mb-2">เลขที่ใบเสร็จ</div>
        <el-input v-model="receiptNo"/>
      </div>
      <div class="col-3">
        <div class="label mb-2">ต้นทุนจริง</div>
        <el-input v-model="cost" :disabled="canNotEditAppointmentCost"/>
      </div>
      <input :value="appointmentId" type="hidden"/>
      <div class="col-1">
          <sc-button class="mt-4" size="sm" variant="warning" @click="handleSubmitAppointmentReceipt"
          >บันทึก</sc-button
          >
      </div>
    </div>
    <div v-else></div>
    <div v-if="!isRead && !$isCallCenter" class="actions">
      <sc-button variant="secondary" @click="handleClickReadButton"
        >อ่านแล้ว</sc-button
      >
    </div>
  </div>
</template>

<script>
import { toLocaleTH } from "@/lib/dateTime";
import ScButton from "@/lib/components/ScButton";
import { Input, DatePicker } from "element-ui";
import { baanRueJaiClient } from "@/dependencies";
import { DateTime } from "luxon";
import { convertPriceStringToInt } from "@/lib/formatter";

export default {
  components: {
    ScButton,
    "el-input": Input,
    "el-date-picker": DatePicker
  },
  props: {
    status: { type: String, default: null },
    service: { type: String, default: null },
    provider: { type: String, default: null },
    user: { type: Object, default: null },
    home: { type: Object, default: null },
    dateTime: { type: [String, Object], default: null },
    route: { type: Object, default: null },
    isRead: { type: Boolean, default: false },
    appointmentId: { type: [String, Number], default: null },
    currentReceiptDate: { type: String, default: null },
    receiptNumber: { type: String, default: null },
    actualCost: { type: [String, Number], default: null },
    mainOption: { type: String, default: null },
    extraOption: { type: String, default: null },
    currentAppointmentTime: { type: String, default: null },
    canNotEditAppointmentCost: { type: Boolean, default: false }
  },
  data() {
    return {
      receiptDate: this.currentReceiptDate || null,
      receiptNo: this.receiptNumber || null,
      cost: this.actualCost || null,
    }
  },
  computed: {
    showStatus() {
      return true;
    },
    statusClassList() {
      if (!this.status) {
        return ["status"];
      }

      return ["status", this.status.toLowerCase()];
    },
    statusMap() {
      return {
        CREATED: "นัดใหม่",
        CANCELLED: "ยกเลิก",
        CONFIRMED: "ยืนยัน",
        WAIT_CONFIRMED_SERVICE_COMPLETE: "ยืนยัน",
        FULFILLED: "เสร็จสิ้น"
      };
    },
    statusText() {
      return this.statusMap[this.status];
    },
    dateTimeTH() {
      return toLocaleTH(this.dateTime);
    },
    dateText() {
      return this.dateTimeTH.day;
    },
    monthText() {
      return this.dateTimeTH.toFormat("MMM");
    },
    timeText() {
      return this.dateTimeTH.hour < 12 ? "ช่วงเช้า" : "ช่วงบ่าย";
    },
    isFulfilledStatus() {
      return this.status === 'FULFILLED';
    },
    optionTitle() {
      if (this.mainOption && this.extraOption) {
        return `${this.mainOption}, ${this.extraOption}`;
      } if (this.mainOption) {
        return this.mainOption;
      }
      return undefined;
    }
  },
  methods: {
    handleClickReadButton() {
      this.$emit("read");
    },
    async handleSubmitAppointmentReceipt() {
      const params = {
        appointment_id: this.appointmentId,
        receipt_date: this.receiptDate
          ? DateTime.fromJSDate(
            new Date(this.receiptDate)
          ).endOf("day").toUTC().toISO()
          : null,
        receipt_number: this.receiptNo || null,
        actual_cost: convertPriceStringToInt(this.cost)
      }

      try {
        await baanRueJaiClient.admin.subscriptions.appointments.putAppointmentReceipt(
          this.appointmentId,
          params
        );

        this.$message.success("บันทึกข้อมูลเรียบร้อย");
      } catch {
        this.$message.error("ไม่สามารถบันทึกข้อมูลได้");
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@/lib/scss/variables";

.updated-appointment-row {
  display: grid;
  grid-template-columns: 110px 300px 2fr 120px;
  grid-gap: $space / 2;
  align-items: center;

  & + & {
    margin-top: $space;
  }
}

.title,
.subtitle {
  line-height: 1.6;
}

.title {
  font-size: 1rem;
}

.subtitle {
  font-size: 0.9rem;
}

.status {
  line-height: 1.2;
  font-weight: bold;
  text-align: center;

  &.created {
    color: $secondary-color;
  }

  &.cancelled {
    color: $danger-color;
  }

  &.confirmed {
    color: $warning-color;
  }

  &.wait_confirmed_service_complete {
    color: $warning-color;
  }

  &.fulfilled {
    color: $success-color;
  }

  .label {
    margin: 0;
    display: block;
    font-weight: bold;
    font-size: 14px;
  }
}

.date {
  width: auto;
}
</style>
